import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';

export interface CaptchaChallenge {
  readonly provider: 'RECAPTCHA_V3';
  readonly siteKey: string;
  readonly action: 'login_password' | 'password_reset';
}

export class AuthApiError extends Error {
  public constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly captcha?: CaptchaChallenge,
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}

async function parseError(response: Response): Promise<AuthApiError> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return new AuthApiError('La demande ne peut pas être traitée.', response.status);
  }

  if (typeof payload === 'object' && payload !== null) {
    const record = payload as Record<string, unknown>;
    const code = typeof record.code === 'string' ? record.code : undefined;
    const captcha = parseCaptchaChallenge(record.captcha);
    return new AuthApiError('La demande ne peut pas être traitée.', response.status, code, captcha);
  }

  return new AuthApiError('La demande ne peut pas être traitée.', response.status);
}

function parseCaptchaChallenge(value: unknown): CaptchaChallenge | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  if (
    record.provider !== 'RECAPTCHA_V3' ||
    typeof record.siteKey !== 'string' ||
    record.siteKey.length < 20 ||
    record.siteKey.length > 256 ||
    (record.action !== 'login_password' && record.action !== 'password_reset')
  ) {
    return undefined;
  }
  return {
    provider: 'RECAPTCHA_V3',
    siteKey: record.siteKey,
    action: record.action,
  };
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method: 'GET',
    credentials: 'same-origin',
  });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as T;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function postJsonWithCsrf<T>(path: string, body: unknown, csrfToken: string): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function mutateWithCsrf(
  method: 'POST' | 'DELETE',
  path: string,
  csrfToken: string,
): Promise<void> {
  const response = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: { 'X-CSRF-Token': csrfToken },
  });
  if (!response.ok) throw await parseError(response);
}

export type SessionViewerAccountKind = 'PLATFORM_SUPERVISOR' | 'ORGANIZATION_MEMBER';
export type SessionViewerOrganizationRole = 'ORGANIZATION_ADMIN' | 'PROVIDER' | 'CLIENT';

export interface SessionViewerResult {
  readonly userId: string;
  readonly givenName: string;
  readonly familyName: string;
  readonly accountKind: SessionViewerAccountKind;
  readonly platformBranding: {
    readonly productName: string;
    readonly markLightUrl: string | null;
  };
  readonly organization: {
    readonly id: string;
    readonly displayName: string;
    readonly role: SessionViewerOrganizationRole;
    readonly branding: {
      readonly logoUrl: string | null;
    };
  } | null;
}

export interface PasswordLoginResult {
  readonly next: 'TOTP';
  readonly expiresAt: string;
}

export interface TotpLoginResult {
  readonly csrfToken: string;
  readonly session: {
    readonly id: string;
    readonly activeOrganizationId: string | null;
    readonly idleExpiresAt: string;
    readonly absoluteExpiresAt: string;
  };
}

export interface PasskeyLoginOptionsResult {
  readonly challengeId: string;
  readonly options: PublicKeyCredentialRequestOptionsJSON;
}

export interface PasskeyRegistrationOptionsResult {
  readonly challengeId: string;
  readonly options: PublicKeyCredentialCreationOptionsJSON;
}

export interface ManagedPasskeyResult {
  readonly id: string;
  readonly displayName: string;
  readonly createdAt: string;
  readonly lastUsedAt: string | null;
}

export interface CurrentSessionResult {
  readonly sessionId: string;
  readonly userId: string;
  readonly activeOrganizationId: string | null;
  readonly trustMode: string;
  readonly idleExpiresAt: string;
  readonly absoluteExpiresAt: string;
  readonly viewer: SessionViewerResult;
}

export interface SessionSummaryResult {
  readonly id: string;
  readonly createdAt: string;
  readonly lastSeenAt: string;
  readonly authenticatedAt: string;
  readonly idleExpiresAt: string;
  readonly absoluteExpiresAt: string;
  readonly revokedAt: string | null;
  readonly activeOrganizationId: string | null;
  readonly trustMode: string;
}

export interface ActivationPreparationResult {
  readonly authenticatorKey: string;
  readonly provisioningUri: string;
  readonly expiresAt: string;
}

export interface ActivationCompletionResult {
  readonly recoveryCodes: readonly string[];
}

export const authApi = {
  currentSession(): Promise<CurrentSessionResult> {
    return getJson('/auth/session');
  },

  listSessions(): Promise<readonly SessionSummaryResult[]> {
    return getJson('/auth/sessions');
  },

  csrfToken(): Promise<{ csrfToken: string }> {
    return getJson('/auth/csrf');
  },

  beginLogin(email: string, password: string, captchaToken?: string): Promise<PasswordLoginResult> {
    return postJson('/auth/login/password', {
      email,
      password,
      ...(captchaToken === undefined ? {} : { captchaToken }),
    });
  },

  completeTotp(code: string, requestExtendedSession: boolean): Promise<TotpLoginResult> {
    return postJson('/auth/login/totp', { code, requestExtendedSession });
  },

  beginPasskeyLogin(): Promise<PasskeyLoginOptionsResult> {
    return postJson('/auth/passkeys/login/options', {});
  },

  completePasskeyLogin(
    challengeId: string,
    response: AuthenticationResponseJSON,
    requestExtendedSession: boolean,
  ): Promise<TotpLoginResult> {
    return postJson('/auth/passkeys/login/complete', {
      challengeId,
      response,
      requestExtendedSession,
    });
  },

  listPasskeys(): Promise<readonly ManagedPasskeyResult[]> {
    return getJson('/auth/passkeys');
  },

  beginPasskeyRegistration(csrfToken: string): Promise<PasskeyRegistrationOptionsResult> {
    return postJsonWithCsrf('/auth/passkeys/registration/options', {}, csrfToken);
  },

  completePasskeyRegistration(
    challengeId: string,
    displayName: string,
    response: RegistrationResponseJSON,
    csrfToken: string,
  ): Promise<ManagedPasskeyResult> {
    return postJsonWithCsrf(
      '/auth/passkeys/registration/complete',
      { challengeId, displayName, response },
      csrfToken,
    );
  },

  revokePasskey(passkeyId: string, csrfToken: string): Promise<void> {
    return mutateWithCsrf('DELETE', `/auth/passkeys/${encodeURIComponent(passkeyId)}`, csrfToken);
  },

  async logout(): Promise<void> {
    const { csrfToken } = await getJson<{ csrfToken: string }>('/auth/csrf');
    return mutateWithCsrf('POST', '/auth/logout', csrfToken);
  },

  revokeSession(sessionId: string, csrfToken: string): Promise<void> {
    return mutateWithCsrf('DELETE', `/auth/sessions/${encodeURIComponent(sessionId)}`, csrfToken);
  },

  revokeAllSessions(csrfToken: string): Promise<void> {
    return mutateWithCsrf('DELETE', '/auth/sessions', csrfToken);
  },

  requestPasswordReset(email: string, captchaToken?: string): Promise<{ accepted: true }> {
    return postJson('/auth/password-reset', {
      email,
      ...(captchaToken === undefined ? {} : { captchaToken }),
    });
  },

  completePasswordReset(token: string, newPassword: string): Promise<void> {
    return postJson('/auth/password-reset/complete', { token, newPassword });
  },

  verifyActivation(token: string): Promise<{ expiresAt: string }> {
    return postJson('/auth/activation/verify-email', { token });
  },

  prepareActivation(token: string, newPassword: string): Promise<ActivationPreparationResult> {
    return postJson('/auth/activation/prepare', { token, newPassword });
  },

  completeActivation(token: string, code: string): Promise<ActivationCompletionResult> {
    return postJson('/auth/activation/complete', { token, code });
  },
};
